import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogPostDetail = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
    axios
      .get(`${API}/blog/${slug}`)
      .then((res) => setPost(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="page-shell pt-32 pb-28" data-testid="blog-post-loading">
        <div className="neu-surface h-[400px] animate-pulse max-w-3xl mx-auto" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="page-shell pt-32 pb-28 text-center" data-testid="blog-post-error">
        <p className="text-body mb-6">This story could not be found.</p>
        <Link to="/blog" className="btn-outline-silver">Return to Blog</Link>
      </div>
    );
  }

  return (
    <div data-testid="page-blog-post">
      <SEO title={post.seo_title || post.title} description={post.seo_description || post.excerpt} path={`/blog/${post.slug}`} />

      <article className="page-shell pt-32 pb-28 max-w-3xl mx-auto">
        <Link to="/blog" className="btn-outline-silver btn-pill-sm inline-flex mb-10" data-testid="blog-post-back-link">
          <ArrowLeft size={14} /> Return to Blog
        </Link>

        <Reveal>
          <span className="eyebrow block mb-5">{post.category}</span>
          <h1 className="text-h1 mb-6">{post.title}</h1>
          <p className="text-small mb-10">
            By {post.author} &middot; {post.published_at ? new Date(post.published_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : ""}
          </p>
        </Reveal>

        {post.cover_image && (
          <Reveal delay={80} className="neu-surface overflow-hidden mb-12" style={{ height: "420px" }}>
            <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
          </Reveal>
        )}

        <Reveal delay={140}>
          <div className="text-editorial text-justify whitespace-pre-line" data-testid="blog-post-content">{post.content}</div>
        </Reveal>

        {post.tags && post.tags.length > 0 && (
          <div className="flex items-center gap-3 mt-12 flex-wrap">
            {post.tags.map((tag) => (
              <span key={tag} className="eyebrow border rounded-full px-4 py-2" style={{ borderColor: "var(--gg-fine-border)" }}>{tag}</span>
            ))}
          </div>
        )}
      </article>
    </div>
  );
};

export default BlogPostDetail;
